import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UpdateUserInput } from '@proref/schemas';
import { listUsers, removeUser, updateUser } from '../services/users.service';

// The roster visible to the caller — every platform user for super_admin, just the caller's own
// federation for federation_admin (the Users page's roster tab). Key is ['users'] per
// convention. `enabled` lets a caller skip the call for a role that can't reach /v1/users.
export function useUsersQuery(enabled = true) {
  return useQuery({
    queryKey: ['users'],
    queryFn: listUsers,
    enabled,
    // A roster changes rarely (invites/signups/removals), so a couple of minutes of staleness is fine.
    staleTime: 2 * 60_000,
  });
}

// Remove a user. Invalidates both ['users'] (the roster itself) and ['invites'] — a redeemed
// invite for the removed user is now orphaned, and listInvites already drops orphaned redeemed
// invites server-side, so refreshing it clears the stale row from the Invite tab's pending list too.
export function useRemoveUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: removeUser,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['users'] });
      void qc.invalidateQueries({ queryKey: ['invites'] });
    },
  });
}

// Edit a roster member, then refresh the list so the table shows the new name/role/tenant.
// Only ['users'] — the caller's own identity comes from the auth context's /me call, not a query
// key, so an admin who renames themselves here keeps the old name in the sidebar until reload.
export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateUserInput }) => updateUser(id, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
}
